/**
 * "Marcar como feito" gera o apontamento (spec 039, adendo 4): o que a tela
 * diz depois, a linha do diálogo, quando "Concluir e encerrar o ticket"
 * aparece e o que cada recusa quer dizer. Módulo puro — a guarda da API
 * (agenda-done-source.test.ts) o importa; por isso nada de `@/` aqui.
 *
 * Tudo sai do que a API DEVOLVEU (`doneAppointmentId`, `ticketMirrored`), e não
 * da tela otimista: quem decide se houve apontamento é o servidor. A API de
 * antes do adendo não manda os dois campos (`undefined`): sem eles, nada de
 * linha de apontamento nem de "Concluir e encerrar" — o padrão seguro durante
 * um rollout em que a tela chegue antes da API.
 */
import type { AgendaEvents, Commitment, PermissionKey } from '@erp/shared';

/** O aviso depois de marcar ou desfazer, pelo compromisso que a API devolveu. */
export function doneNotice(before: Pick<Commitment, 'doneAppointmentId'>, after: Commitment): string {
  if (after.done) {
    if (after.doneAppointmentId != null) return `Feito. Apontamento registrado no ticket #${after.ticketNumber}.`;
    if (after.ticketMirrored) return 'Feito. O apontamento deste ticket é feito no TiFlux.';
    return 'Feito.';
  }
  return before.doneAppointmentId != null
    ? `Feito desfeito. O apontamento saiu do ticket #${after.ticketNumber}.`
    : 'Feito desfeito.';
}

/** O aviso do "Concluir e encerrar o ticket". */
export function concludeNotice(after: Commitment): string {
  return `Feito. Apontamento registrado no ticket #${after.ticketNumber}, e o ticket foi encerrado.`;
}

/**
 * A linha do diálogo que mostra que o apontamento existe. O feito de antes do
 * adendo (sem apontamento) não ganha linha nenhuma.
 */
export function doneAppointmentLine(c: Commitment): string | null {
  if (!c.done) return null;
  if (c.doneAppointmentId != null) return `Apontamento registrado no ticket #${c.ticketNumber}`;
  if (c.ticketMirrored) return 'O apontamento deste ticket é feito no TiFlux';
  return null;
}

/**
 * "Concluir e encerrar o ticket" aparece com o ticket aberto e editável no
 * ERP (o espelhado se encerra no TiFlux; o oculto é sempre espelhado) e para
 * quem pode apontar E encerrar — as keys de POST /tickets/:n/appointments e de
 * POST /tickets/:n/close. Esconder é conveniência: quem barra é a API.
 */
export function canConcludeTicket(c: Commitment, can: (key: PermissionKey) => boolean): boolean {
  return (
    // `=== false`, e não `!`: a API de antes do adendo não diz, e sem saber não se oferece
    c.ticketMirrored === false &&
    c.ticketState === 'open' &&
    can('appointments.create_edit') &&
    can('tickets.view_open')
  );
}

/** A confirmação: o que vai acontecer, e que as automações de encerramento rodam. */
export function concludeConfirmText(c: Commitment): string {
  return (
    `O agendamento fica feito, o apontamento "Serviço concluído" entra no ticket #${c.ticketNumber} ` +
    'e o ticket é encerrado. As automações de encerramento da mesa rodam (pesquisa de satisfação ' +
    'ou aviso ao cliente), como no "Fechar ticket" do ticket.'
  );
}

/** Rótulos das recusas do "feito" e do "concluir"; null = o rótulo de sempre (`errorMessage`). */
export function doneErrorMessage(err: unknown): string | null {
  if (err === null || typeof err !== 'object' || !('error' in err)) return null;
  const code = (err as { error: unknown }).error;
  const details = (err as { details?: { permission?: unknown } }).details;
  if (code === 'permission_denied') {
    switch (details?.permission) {
      case 'appointments.create_edit':
        return 'Você não tem permissão para lançar apontamentos, e o "feito" registra um no ticket. Nada foi marcado.';
      case 'appointments.edit_all':
        return 'O apontamento deste "feito" é de outra pessoa, e você não tem permissão para apagá-lo. Nada foi desfeito.';
      case 'tickets.view_open':
        return 'Você não tem permissão para encerrar tickets. Nada foi feito.';
      default:
        return null;
    }
  }
  switch (code) {
    case 'ticket_not_found':
      return 'Você não tem acesso a este ticket, e o "feito" registra um apontamento nele. Nada foi feito.';
    case 'desk_has_no_closed_status':
      return 'A mesa deste ticket não tem um status de fechado. Nada foi feito.';
    case 'ticket_not_open':
      return 'O ticket já está encerrado ou cancelado. Nada foi feito.';
    // spec 054: o motivo de fechamento da mesa do ticket
    case 'close_reason_required':
      return 'Escolha o motivo do fechamento: a mesa deste ticket exige um. Nada foi feito.';
    case 'close_reason_invalid':
      return 'Este motivo não vale mais para a mesa do ticket. Recarregue e escolha outro. Nada foi feito.';
    default:
      return null;
  }
}

/** O compromisso que a API devolveu, no lugar do que estava no cache da agenda. */
export function withCommitment(events: AgendaEvents | undefined, saved: Commitment): AgendaEvents | undefined {
  if (!events) return events;
  return {
    ...events,
    commitments: events.commitments.map((c) => (c.id === saved.id ? saved : c)),
  };
}
